<template>
	<div>
		<b-card-header header-tag="header" class="p-0 mt-2" role="tab">
			<b-row class="justify-content-end">
				<b-button
          :data-cy="`${carrier.name}-add-iccid-range-btn`"
					v-if="isPortalAdmin"
					size="sm"
					variant="success"
					class="mt-2 mb-2 mr-4"
					title="Add ICCID Range"
					@click="addIccid = true"
					:disabled="addIccid"
				>
					<i class="fas fa-plus"></i> New
				</b-button>
			</b-row>
			<base-button block type="primary" size="sm" class="border-non-radius text-white text-left">
				ICCID Ranges
				<base-button
					type="warning"
					size="sm"
					class="float-right"
					v-on:click="updateIccidList"
					title="Refresh Results"
					v-b-tooltip.hover
				>
					<i class="fa fa-recycle"></i>
				</base-button>
			</base-button>
		</b-card-header>

    <b-card-body v-if="isLoading" class="text-center">
      <b-icon icon="three-dots" animation="cylon" font-scale="4"/>
    </b-card-body>

		<b-card-body v-if="!isLoading" class="text-center pl-0 pr-0 pt-2 pb-2">
			<span v-for="(item, index) in iccids" :key="index" :state="item">
				<template>
					<b-row class="mt-2 mb-3">
						<b-col cols="5" lg="5" class="iccid-left-input">
							<b-form-input
								v-model="item.start"
								type="text"
								size="sm"
								maxLength="19"
								:disabled="item.disabled > 0 || !isPortalAdmin"
								:state="validateLeftState(item.start)"
							></b-form-input>
						</b-col>
						<b-col cols="1" lg="1" class="iccid-line"> - </b-col>
						<b-col cols="5" lg="5" class="iccid-right-input">
							<b-form-input
								v-model="item.end"
								type="text"
								size="sm"
								maxLength="19"
								:disabled="item.disabled > 0 || !isPortalAdmin"
								:state="validateRightState(item.start, item.end)"
							></b-form-input>
						</b-col>
						<b-col
							v-if="
								(item.start != item.previous.start || item.end != item.previous.end) &&
								validateRightState(item.start, item.end) &&
								validateLeftState(item.start)
							"
							cols="1"
							lg="1"
							class="iccid-recycle"
						>
							<base-button
								type="primary"
								class="vertical-dot-button"
								title="Save Updated ICCID Range"
								v-on:click="updateIccid(index, item)"
							>
								<b-icon icon="arrow-bar-up"></b-icon>
							</base-button>
							<base-button
								type="warning"
								class="vertical-dot-button"
								title="Cancel Editing"
								v-on:click="resetRow(item)"
							>
								<b-icon icon="arrow-counterclockwise"></b-icon>
							</base-button>
						</b-col>
						<b-col v-else cols="1" lg="1">
							<b-button
								class="vertical-dot-button"
								variant="primary"
								:id="`${index}-${carrier.name}-popover`"
								:disabled="!isPortalAdmin"
							>
								<b-icon icon="three-dots-vertical" class="font-16"></b-icon>
							</b-button>
							<b-popover
								:target="`${index}-${carrier.name}-popover`"
								triggers="click blur"
								placement="top"
							>
								<b-row class="mx-2 mt-2 justify-content-center">
									<base-button class="w-100" type="link" size="sm" v-on:click="showDisableRangeModal(item)">
										<span v-if="Number(item.disabled) === 1">
											<!-- <b-icon icon="check"></b-icon> -->
											Enable Range
										</span>
										<span v-else>
											<!-- <b-icon icon="x"></b-icon> -->
											Disable Range
										</span>
									</base-button>
								</b-row>
								<b-row class="mx-2 mt-2 justify-content-center">
									<base-button
										class="w-100"
										type="link"
										size="sm"
										v-on:click="removeIccid(carrier, item)"
									>
										<!-- <b-icon icon="trash" class="font-16"></b-icon> -->
										Delete Range
									</base-button>
								</b-row>
							</b-popover>
						</b-col>
					</b-row>
				</template>
			</span>

			<template v-if="addIccid">
				<b-row class="mt-2 mb-3">
					<b-col cols="5" lg="5" class="iccid-left-input text-left">
						<b-form-group>
							<b-form-input
								v-model="newIccid.start"
								type="text"
								size="sm"
								maxLength="20"
								:state="validateLeftState(newIccid.start)"
							></b-form-input>
							<b-form-invalid-feedback :state="validateLeftState(newIccid.start)">
								Please enter a valid ICCID &nbsp;
								<b-icon
									icon="question-octagon"
									variant="info"
									class="font"
									v-b-popover.hover.right="'ICCID must start with 89XXX'"
								></b-icon>
							</b-form-invalid-feedback>
						</b-form-group>
					</b-col>
					<b-col cols="1" lg="1" class="iccid-line"> - </b-col>
					<b-col cols="5" lg="5" class="iccid-right-input pr-0 pl-0 text-left">
						<b-form-group>
							<b-form-input
								v-model="newIccid.end"
								type="text"
								size="sm"
								maxLength="20"
								:state="validateRightState(newIccid.start, newIccid.end)"
							></b-form-input>
							<b-form-invalid-feedback :state="validateRightState(newIccid.start, newIccid.end)">
								Please enter a valid ICCID &nbsp;
								<b-icon
									icon="question-octagon"
									variant="info"
									class="font"
									v-b-popover.hover.right="
										'ICCID must start with 89XXX. And end ICCID Should be bigger than start ICCID'
									"
								></b-icon>
							</b-form-invalid-feedback>
						</b-form-group>
					</b-col>
					<b-col cols="1" lg="1" class="iccid-recycle">
						<base-button
							type="danger"
							class="vertical-dot-button"
							title="Cancel"
							v-on:click="cancelRangeAdd"
						>
							<b-icon icon="x" class="font-16"></b-icon>
						</base-button>
					</b-col>
				</b-row>
			</template>
		</b-card-body>
		<b-row v-if="addIccid">
			<b-col cols="9"></b-col>
			<b-col>
				<base-button
					type="primary"
					size="sm"
					class="w-100 mt-2"
					title="Save New ICCID Range"
					v-on:click="saveIccid"
					:disabled="!submitDisabled"
				>
					<b-icon icon="check" class="font-16"></b-icon>
					Save
				</base-button>
			</b-col>
		</b-row>
	</div>
</template>

<script>
import Swal from "sweetalert2";
import Toast from "../../../Components/Toast.vue";
import otaDashboardService from "../../../../common/api-services/ota-dashboard-service";

export default {
	name: "CarrierIccids",
	props: {
    active_carrier: {
      type: String,
      required: true,
    },
		carrier: {
			type: Object,
			required: true,
		},
		isPortalAdmin: {
			type: Boolean,
			default: false,
		},
	},
  watch: {
    active_carrier() {
      if (this.active_carrier !== this.carrier.name) return;
      this.iccids = [];
      this.getIccidRanges();
    }
  },
	mounted() {
		// console.log(this.carrier);
		this.getIccidRanges();
	},
	computed: {
		submitDisabled() {
			return (
				this.validateLeftState(this.newIccid.start) &&
				this.validateRightState(this.newIccid.start, this.newIccid.end)
			);
		},
	},
	data() {
		return {
			iccids: [],
			newIccid: {
				start: "",
				end: "",
				removed: false,
			},
			addIccid: false,
			modifyRow: [],
			rangeUpdate: {
				start: "",
				end: "",
			},
      isLoading: false,
		};
	},
	methods: {
		async getIccidRanges() {
      if (this.active_carrier !== this.carrier.name) return;
      this.iccids = [];
      this.isLoading = true;
			// console.log("Carrier ", this.carrier)
			if (!this.carrier.name) return;
			await otaDashboardService
				.listIccidRanges({carrier: this.carrier.name})
				.then(res => {
					// console.log("ICCID Ranges ", res.data)
					if (res && res.data.status) {
						res.data.result.ranges.map(item => {
							let template = "0000000000000000000";
							let begin = `${item.iccid_begin}${template.substring(item.iccid_begin.length)}`;
							let start = item.iccid_start;
							let end = item.iccid_end;
							// let test = begin.substring(0, begin.length - end.length) + end
							// console.log("Test ", test)
							let iccid = {
								id: item.id,
								start: begin.substring(0, begin.length - start.length) + start,
								end: begin.substring(0, begin.length - end.length) + end,
								disabled: Number(item.disabled),
								removed: false,
							};
							const previous = {...iccid};
							iccid.previous = previous; //structuredClone(iccid);
							this.iccids.push(iccid);
						});
					} else {
						console.log("Error ", this.carrier.name);
						console.log("Error ", res);
						this.$toast.error({
							component: Toast,
							props: {
								subtitle: "ERROR:",
								description: res.data.error_string,
							},
						});
					}
				})
				.catch(err => {
					console.log("Error ", this.carrier.name);
					console.error("Error ", err);
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: "ERROR:",
							description: err.message,
						},
					});
				})
        .finally(() => {
          this.isLoading = false;
        });
		},

		async updateIccidList() {
			let newiccids = [];
      this.isLoading = true;
			await otaDashboardService.listIccidRanges({carrier: this.carrier.name}).then(res => {
				// console.log("ICCID Ranges ", res.data)
				if (res && res.data.status) {
					res.data.result.ranges.map(item => {
						let template = "0000000000000000000";
						let begin = `${item.iccid_begin}${template.substring(item.iccid_begin.length)}`;
						let start = item.iccid_start;
						let end = item.iccid_end;
						// let test = begin.substring(0, begin.length - end.length) + end
						// console.log("Test ", test)
						let iccid = {
							id: item.id,
							start: begin.substring(0, begin.length - start.length) + start,
							end: begin.substring(0, begin.length - end.length) + end,
							disabled: Number(item.disabled),
							removed: false,
						};
						const previous = {...iccid};
						iccid.previous = previous; //structuredClone(iccid);
						newiccids.push(iccid);
					});
					this.iccids = [...newiccids];
				}
			})
      .catch(err => {
        console.log("Error ", this.carrier.name);
        console.error("Error ", err);
        this.$toast.error({
          component: Toast,
          props: {
            subtitle: "ERROR:",
            description: err.message,
          },
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
		},

		updateIccidDisabled(item) {
			if (this.rangeUpdate.start === item.start && this.rangeUpdate.end === item.end) {
				return true;
			} else {
				return (
					!this.validateLeftState(this.rangeUpdate.start) &&
					!this.validateRightState(this.rangeUpdate.start, this.rangeUpdate.end)
				);
			}
		},

		validateLeftState(itemStart) {
			// validate that this value does not overlap with any other ranges.
			this.iccids.map(item => {
				if (item.start.toString().startsWith(itemStart)) return false;
				if (item.end.toString().startsWith(itemStart)) return false;
			});

			let pre = itemStart.substr(0, 2);
			if (pre === "89" && itemStart.length === 19) {
				return true;
			} else {
				return false;
			}
		},

		validateRightState(leftItem, rightItem) {
			//debugger;
			// validate that this value does not overlap with any other ranges.
			this.iccids.map(item => {
				// console.log('item.start: '+item.start+' rightItem:'+rightItem);
				// console.log('item.end: '+item.end+' rightItem:'+rightItem);
				if (item.start.toString().startsWith(rightItem)) return false;
				if (item.end.toString().startsWith(rightItem)) return false;
			});

			let pre = rightItem.substr(0, 2);
			if (rightItem > leftItem && pre === "89" && rightItem.length === 19) {
				return true;
			} else {
				return false;
			}
		},

		cancelRangeAdd() {
			this.addIccid = false;
			this.newIccid = {
				start: "",
				end: "",
				removed: false,
			};
		},

		async saveIccid() {
			let payload = {
				carrier: this.carrier.name,
				iccid_start: this.newIccid.start,
				iccid_end: this.newIccid.end,
			};
			// console.log("Save ICCID ", payload);
			otaDashboardService.addIccidRange(payload).then(res => {
				if (res && res.data.status) {
					this.$toast.success({
						component: Toast,
						props: {
							subtitle: "PASS:",
							description: "ICCID range added successfully.",
						},
					});
					this.updateIccidList(); //this.$root.$emit("getCarrierList_API");
					this.cancelRangeAdd();
				} else {
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: "ERROR:",
							description: res.data.error_string,
						},
					});
				}
			});
		},

		async updateIccid(index, iccid) {
			// this.modifyRow = this.modifyRow.filter(item => item !== index);
			// console.log("Original ICCID ", structuredClone(iccid));
			let payload = {
				id: iccid.id,
				carrier: this.carrier.name,
				iccid_start: iccid.start,
				iccid_end: iccid.end,
			};
			// console.log("Update ICCID ", payload);
			otaDashboardService.editIccidRange(payload).then(res => {
				if (res && res.data.status) {
					this.$toast.success({
						component: Toast,
						props: {
							subtitle: "PASS:",
							description: "ICCID range updated successfully.",
						},
					});
					this.updateIccidList(); //this.$root.$emit("getCarrierList_API");
				} else {
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: "ERROR:",
							description: res.data.error_string,
						},
					});
				}
			});
		},

		async disableRange(payload) {
			// console.log("Disable Range ", payload)
			otaDashboardService.disableIccidRange(payload).then(res => {
				if (res && res.data.status) {
					this.$toast.success({
						component: Toast,
						props: {
							subtitle: "PASS:",
							description: `ICCID range ${payload.disabled > 0? "disabled": "enabled"} successfully.`,
						},
					});
					this.updateIccidList(); //this.$root.$emit("getCarrierList_API");
				} else {
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: "ERROR:",
							description: res.data.error_string,
						},
					});
				}
			});
		},

		showDisableRangeModal(range) {
			let payload = {
				id: range.id,
				carrier: this.carrier.name,
				disabled: Number(range.disabled) === 1 ? 0 : 1,
			};
			// console.log(payload);
			if (payload.disabled === 1) {
				Swal.fire({
					title: `Disable ICCID Range`,
					text: `This will disable the ICCID range and make it completely inaccessible for new provisions. Are you sure you want to continue?`,
					showCloseButton: true,
					showCancelButton: true,
					confirmButtonText: "Ok",
					cancelButtonText: "Cancel",
					customClass: {
						cancelButton: 'order-1 mr-3',
						confirmButton: 'btn btn-danger order-2',
					},
				}).then(result => {
					if (result.isConfirmed) {
						this.disableRange(payload);
					}
				});
			} else {
				this.disableRange(payload);
			}
		},

		removeIccid(carrier, iccid) {
			Swal.fire({
				title: `Remove ICCID Range`,
				text: `Are you sure you want to completely remove this ICCID range?`,
				showCloseButton: true,
				showCancelButton: true,
				confirmButtonText: "DELETE",
				cancelButtonText: "Cancel",
				customClass: {
					cancelButton: 'order-1 mr-3',
					confirmButton: 'btn btn-danger order-2',
				},
			}).then(result => {
				if (result.isConfirmed) {
					if (carrier) {
						let payload = {
							id: iccid.id,
							carrier: carrier.name,
						};
						// console.log("Remove ICCID ", payload)
						otaDashboardService.deleteIccidRange(payload).then(res => {
							if (res && res.data.status) {
								this.$toast.success({
									component: Toast,
									props: {
										subtitle: "PASS:",
										description: "ICCID range removed successfully.",
									},
								});
								this.updateIccidList(); //this.$root.$emit("getCarrierList_API");
							} else {
								this.$toast.error({
									component: Toast,
									props: {
										subtitle: "ERROR:",
										description: res.data.error_string,
									},
								});
							}
						});
					}
				}
			});
		},

		handleRowChange(index) {
			if (!this.modifyRow.includes(index)) {
				this.modifyRow.push(index);
			}
		},

		setRangeUpdate(item, index) {
			this.modifyRow.push(index);
			this.rangeUpdate = {
				start: item.start,
				end: item.end,
			};
		},

		resetRow(item) {
			// this.rangeUpdate = {
			// 	start: "",
			// 	end: "",
			// };
			// this.modifyRow = this.modifyRow.filter(item => item !== index);
			let previous = item.previous;
			item.start = previous.start;
			item.end = previous.end;
		},
	},
};
</script>
